/**
 * Architectural line art.
 *
 * The rebuild ships with no photography: all 134 images the old site referenced
 * return HTTP 404 from the origin (the whole `wp-content/uploads` tree is gone)
 * and the Internet Archive has no copy either. Rather than pad the design with
 * unrelated stock photos, the visual language is drawn - technical elevations
 * and plans in hairline strokes with gold accents, which suits a property
 * manager and costs about 3 KB per illustration.
 *
 * If the original photographs are recovered, these are the components to
 * replace; see web/public/images/README.md for the slots and their aspect
 * ratios.
 */

const HAIRLINE = 'rgba(255, 255, 255, 0.26)'
const HAIRLINE_SOFT = 'rgba(255, 255, 255, 0.13)'
const GOLD = '#ccad00'

/** Grid of windows for a building block. */
function Windows({ x, y, cols, rows, w = 14, h = 11, gapX = 10, gapY = 12, lit = [] }) {
  const cells = []
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const index = row * cols + col
      const isLit = lit.includes(index)
      cells.push(
        <rect
          key={`${row}-${col}`}
          x={x + col * (w + gapX)}
          y={y + row * (h + gapY)}
          width={w}
          height={h}
          rx="1"
          fill={isLit ? GOLD : 'none'}
          fillOpacity={isLit ? 0.85 : 0}
          stroke={isLit ? GOLD : HAIRLINE}
          strokeWidth="1"
        />,
      )
    }
  }
  return <g>{cells}</g>
}

/* -------------------------------------------------------------------------- */
/* Hero - a street elevation of three blocks                                  */
/* -------------------------------------------------------------------------- */

export function HeroArt({ className }) {
  const ground = 404

  return (
    <svg
      className={className}
      viewBox="0 0 520 440"
      fill="none"
      role="img"
      aria-label="Illustration: Aufriss von drei Wohngebäuden"
    >
      {/* soft gold wash behind the middle block */}
      <defs>
        <radialGradient id="heroGlow" cx="50%" cy="40%" r="55%">
          <stop offset="0%" stopColor={GOLD} stopOpacity="0.22" />
          <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="heroFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.07" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.01" />
        </linearGradient>
      </defs>
      <circle cx="265" cy="180" r="180" fill="url(#heroGlow)" />

      {/* left block */}
      <g>
        <path
          d="M46 150h140v254H46z"
          fill="url(#heroFade)"
          stroke={HAIRLINE}
          strokeWidth="1.4"
        />
        <path d="M46 150 116 116l70 34" stroke={HAIRLINE} strokeWidth="1.4" />
        <Windows x={62} y={172} cols={5} rows={7} lit={[7, 18]} />
        {/* entrance */}
        <path d="M100 404v-34h32v34" stroke={HAIRLINE} strokeWidth="1.4" />
        <path d="M116 370v34" stroke={HAIRLINE_SOFT} strokeWidth="1" />
      </g>

      {/* middle block - the tallest, carries the gold roofline */}
      <g>
        <path
          d="M196 74h132v330H196z"
          fill="url(#heroFade)"
          stroke={HAIRLINE}
          strokeWidth="1.4"
        />
        <path d="M190 74h144" stroke={GOLD} strokeWidth="2.4" />
        <Windows x={210} y={98} cols={4} rows={9} lit={[2, 9, 14, 27]} />
        <path d="M244 404v-40h36v40" stroke={HAIRLINE} strokeWidth="1.4" />
        <path
          d="M252 364v40M262 364v40M272 364v40"
          stroke={HAIRLINE_SOFT}
          strokeWidth="1"
        />
      </g>

      {/* right block, set back */}
      <g opacity="0.85">
        <path
          d="M338 206h136v198H338z"
          fill="url(#heroFade)"
          stroke={HAIRLINE}
          strokeWidth="1.4"
        />
        <path d="M338 206h136l-14-22H352z" stroke={HAIRLINE} strokeWidth="1.4" />
        <Windows x={354} y={228} cols={4} rows={5} lit={[6]} />
        <path d="M388 404v-30h30v30" stroke={HAIRLINE} strokeWidth="1.4" />
      </g>

      {/* ground */}
      <path d={`M12 ${ground}h496`} stroke={HAIRLINE} strokeWidth="1.4" />
      <path
        d={`M12 ${ground + 12}h74M110 ${ground + 12}h150M286 ${ground + 12}h96M406 ${ground + 12}h102`}
        stroke={HAIRLINE_SOFT}
        strokeWidth="1"
      />

      {/* dimension marker, the detail that makes it read as a drawing */}
      <g stroke={GOLD} strokeWidth="1" opacity="0.65">
        <path d="M196 56h132M196 50v12M328 50v12" />
      </g>
    </svg>
  )
}

/* -------------------------------------------------------------------------- */
/* Service illustrations                                                      */
/* -------------------------------------------------------------------------- */

/** WEG - several units under one roof. */
function BuildingArt() {
  return (
    <svg
      viewBox="0 0 480 300"
      fill="none"
      role="img"
      aria-label="Illustration: Mehrparteienhaus im Aufriss"
    >
      <path d="M70 90h180v186H70z" stroke={HAIRLINE} strokeWidth="1.4" />
      <path d="M60 90 160 44l100 46" stroke={HAIRLINE} strokeWidth="1.4" />
      <path d="M60 90h200" stroke={GOLD} strokeWidth="2" />
      <Windows x={88} y={112} cols={4} rows={4} w={22} h={16} gapX={14} gapY={20} lit={[1, 10]} />
      <path d="M142 276v-32h36v32" stroke={HAIRLINE} strokeWidth="1.4" />

      <path d="M270 140h150v136H270z" stroke={HAIRLINE} strokeWidth="1.4" />
      <path d="M262 140h166" stroke={HAIRLINE} strokeWidth="1.4" />
      <Windows x={288} y={162} cols={3} rows={3} w={24} h={16} gapX={20} gapY={20} lit={[4]} />
      <path d="M330 276v-26h30v26" stroke={HAIRLINE} strokeWidth="1.4" />

      <path d="M24 276h432" stroke={HAIRLINE} strokeWidth="1.4" />
      <g stroke={GOLD} strokeWidth="1" opacity="0.6">
        <path d="M70 300h180M70 294v12M250 294v12" />
      </g>
    </svg>
  )
}

/** Sondereigentum - a single unit, drawn as a plan. */
function FloorPlanArt() {
  return (
    <svg
      viewBox="0 0 480 300"
      fill="none"
      role="img"
      aria-label="Illustration: Grundriss einer Eigentumswohnung"
    >
      {/* outer wall */}
      <path d="M60 40h360v220H60z" stroke={HAIRLINE} strokeWidth="2" />
      {/* the highlighted unit */}
      <path
        d="M60 40h150v130H60z"
        fill={GOLD}
        fillOpacity="0.1"
        stroke={GOLD}
        strokeWidth="1.6"
      />
      {/* internal partitions */}
      <path
        d="M210 40v220M60 170h150M210 150h210M330 150v110"
        stroke={HAIRLINE}
        strokeWidth="1.4"
      />
      {/* door openings - gaps drawn as arcs */}
      <path d="M210 96a18 18 0 0 1 18 18" stroke={HAIRLINE_SOFT} strokeWidth="1" />
      <path d="M116 170a18 18 0 0 1 18 18" stroke={HAIRLINE_SOFT} strokeWidth="1" />
      {/* fixtures */}
      <rect x="248" y="176" width="56" height="34" rx="3" stroke={HAIRLINE} strokeWidth="1" />
      <rect x="356" y="176" width="42" height="42" rx="3" stroke={HAIRLINE} strokeWidth="1" />
      <circle cx="120" cy="88" r="22" stroke={GOLD} strokeWidth="1.2" opacity="0.8" />
      {/* windows on the outer wall */}
      <path
        d="M96 40h48M256 40h60M420 96v44M60 200v36"
        stroke={GOLD}
        strokeWidth="2.4"
      />
      <g stroke={GOLD} strokeWidth="1" opacity="0.6">
        <path d="M60 284h150M60 278v12M210 278v12" />
      </g>
    </svg>
  )
}

/** Mietverwaltung - the same asset, read as a yield. */
function YieldArt() {
  const bars = [
    { x: 92, h: 54 },
    { x: 152, h: 82 },
    { x: 212, h: 104 },
    { x: 272, h: 138 },
    { x: 332, h: 168 },
  ]
  const base = 244

  return (
    <svg
      viewBox="0 0 480 300"
      fill="none"
      role="img"
      aria-label="Illustration: steigende Erträge eines Mehrfamilienhauses"
    >
      {/* building silhouette behind the chart */}
      <g opacity="0.4">
        <path d="M60 96h120v148H60z" stroke={HAIRLINE} strokeWidth="1.4" />
        <path d="M52 96 120 62l68 34" stroke={HAIRLINE} strokeWidth="1.4" />
        <Windows x={76} y={116} cols={3} rows={3} w={20} h={14} gapX={16} gapY={18} />
      </g>

      {bars.map((bar, index) => {
        const last = index === bars.length - 1
        return (
          <rect
            key={bar.x}
            x={bar.x}
            y={base - bar.h}
            width="38"
            height={bar.h}
            rx="2"
            fill={last ? GOLD : '#ffffff'}
            fillOpacity={last ? 0.85 : 0.07}
            stroke={last ? GOLD : HAIRLINE}
            strokeWidth="1.2"
          />
        )
      })}

      {/* trend line through the bar tops */}
      <path
        d={`M111 ${base - 54 - 14} L171 ${base - 82 - 14} L231 ${base - 104 - 14} L291 ${base - 138 - 14} L351 ${base - 168 - 14}`}
        stroke={GOLD}
        strokeWidth="1.6"
        strokeDasharray="5 5"
        opacity="0.75"
      />
      <circle cx="351" cy={base - 168 - 14} r="4" fill={GOLD} />

      <path d={`M40 ${base}h400`} stroke={HAIRLINE} strokeWidth="1.4" />
      <path
        d={`M40 ${base - 60}h400M40 ${base - 120}h400M40 ${base - 180}h400`}
        stroke={HAIRLINE_SOFT}
        strokeWidth="1"
        strokeDasharray="3 7"
      />
    </svg>
  )
}

const ART = {
  building: BuildingArt,
  apartment: FloorPlanArt,
  keys: YieldArt,
}

/** Pick a service illustration by the `art` key used in pages/Verwalten.jsx. */
export function ServiceArt({ variant }) {
  const Component = ART[variant] ?? BuildingArt
  return <Component />
}
